import { Component, inject, OnDestroy } from '@angular/core';

import { ReplaySubject } from 'rxjs';

import {
    ApiFragment,
    DialogStyle,
    ExtendedBaseProfileField,
    ExtendedProfileField,
    FieldDefinition,
    FieldReadHelper,
    LocationDialogFailureReason,
    LocationDialogOutput,
    NotificationType,
    PartialRecord,
    ShDynamicDialogService,
    ShLocationDialogComponent,
    ShPositionSubFieldsDialogComponent,
    ShSimpleReadonlyFieldComponent
} from '@stylehaven/components';
import { SimpleType } from '../../../../common/entities/common/simple-type.entity';
import { BaseSandbox } from '../../../../common/sandboxes/base/base.sandbox.service';
import { MHSearchRoutingService } from '../../../../common/services/common/search-routing.service';

@Component({
    standalone: false,
    selector: 'med-address-readonly-field',
    templateUrl: './address-readonly-field.component.html',
    styleUrls: ['./address-readonly-field.component.scss']
})
export class AddressReadonlyFieldComponent extends ShSimpleReadonlyFieldComponent implements OnDestroy {
    public locationFieldDefinition$ = new ReplaySubject<FieldDefinition | undefined>(1);

    private readonly FULL_ADDRESS_FIELD_KEY = 'Address';

    private readonly sandbox: BaseSandbox = inject(BaseSandbox);

    constructor(
        protected fieldHelper: FieldReadHelper,
        protected searchRoutingService: MHSearchRoutingService,
        private dynamicDialogService: ShDynamicDialogService
    ) {
        super(fieldHelper, searchRoutingService);
    }

    init(profileField: ExtendedBaseProfileField, record: PartialRecord): void {
        const location: SimpleType | undefined = this.fieldHelper
            .getMapFieldValue(profileField.FieldDefinition, record, null)
            ?.find(typedValue => typedValue.type === this.FULL_ADDRESS_FIELD_KEY)?.value;

        const locationFieldDefinition: FieldDefinition | undefined = profileField.FieldDefinition.Children?.find(
            child => child.Key === this.FULL_ADDRESS_FIELD_KEY
        );

        this.value$.next(location);
        this.locationFieldDefinition$.next(locationFieldDefinition);
    }

    public openLocationDialog(fragment: PartialRecord, positionFieldDefinition: FieldDefinition): void {
        this.dynamicDialogService
            .open<LocationDialogOutput>(ShLocationDialogComponent, {
                data: {
                    fragment,
                    positionFieldDefinition
                },
                options: {
                    style: DialogStyle.ContentOnly
                }
            })
            .afterClosed$.pipe()
            .subscribe(result => {
                if (result?.result === 'failed' && result?.reason === LocationDialogFailureReason.NO_COORDINATE) {
                    this.sandbox.createAndAddNewToast('form.location.invalid-location', NotificationType.Error);
                }
            });
    }

    public openSubFieldDialog(
        record: ApiFragment,
        profileField: ExtendedProfileField,
        fullAddress: string,
        locationFieldDefinition: FieldDefinition
    ): void {
        this.dynamicDialogService.open(ShPositionSubFieldsDialogComponent, {
            data: {
                record,
                profileField,
                fullAddress,
                fullAddressLabel: locationFieldDefinition.Label
            },
            options: {
                style: DialogStyle.Wide
            },
            title: this.translateService.translate('modules.general.form.location.subfields-dialog-title', {
                fielddef: profileField.DisplayLabel
            })
        });
    }

    public ngOnDestroy(): void {
        this.locationFieldDefinition$.complete();
        super.ngOnDestroy();
    }
}
